import { MatterStatus } from "./matter-type";
import { DateRange } from "./date-range";

export type Sorter = {
  field: keyof MatterOrder;
  order: AscDesc;
};

export enum AscDesc {
  asc = "asc",
  desc = "desc",
}

interface Count {
  _count?: AscDesc | null;
}

export interface ExternalMatterOrderByRelationAggregateInput extends Count {}

export interface MatterOrderByRelationAggregateInput extends Count {}

export interface RateOrderByRelationAggregateInput extends Count {}

export interface NotificationRuleClientsOrderByRelationAggregateInput extends Count {}

export interface UserOrderByWithRelationInput {
  displayName?: AscDesc | undefined;
}

export interface ClientOrderByWithRelationInput {
  name?: AscDesc | null;
}

export interface MatterOrder {
  updatedAt?: AscDesc | null;
  client?: ClientOrderByWithRelationInput | null;
  createdBy?: UserOrderByWithRelationInput | null;
}

export interface MatterFilter {
  currentPage?: number;
  pageSize?: number;
  title: string | null;
  client: number[] | null;
  orgUnit: number | null;
  createdBy: number[] | null;
  status: MatterStatus[] | null;
  updatedAt: DateRange | null;
  orderBy: MatterOrder[] | null;
  currencyId: number[] | null;
  feeEarner: number[] | null;
  someTimeEntries: boolean | null;
  linked: boolean | null;
}

export function buildFilter(
  pagination: { current: number; pageSize: number },
  filters: any,
  sorter: Sorter
): MatterFilter {
  const orderBy = newOrderBy(sorter);
  const filter: MatterFilter = {
    currentPage: pagination.current,
    pageSize: pagination.pageSize,
    title: newTitleFilter(filters.title),
    client: newClientFilter(filters.client),
    orgUnit: null,
    status: filters.status,
    updatedAt: newDateRangeFilter(filters.updatedAt),
    createdBy: newUserFilter(filters.createdBy),
    currencyId: newCurrencyFilter(filters.currency),
    feeEarner: newFeeEarnerFilter(filters.feeEarner),
    orderBy: orderBy ? [orderBy] : null,
    someTimeEntries: filters.someTimeEntries,
    linked: filters.linked,
  };
  return filter;
}

export function newTitleFilter(title: string) {
  if (title) {
    return title;
  }
  return null;
}

export function newClientFilter(clientIds: string[]) {
  return clientIds ? clientIds.map((id) => parseInt(id)) : null;
}

export function newUserFilter(userIds: string[]) {
  return userIds ? userIds.map((id) => parseInt(id)) : null;
}

export function newDateRangeFilter(dates: number[]): DateRange | null {
  if (dates && dates.length > 0) {
    const from = dates[0] > 0 ? new Date(dates[0]) : null;
    const to = dates.length > 1 && dates[1] > 0 ? new Date(dates[1]) : null;
    return { from, to };
  }
  return null;
}

export function newCurrencyFilter(currencyIds: string[]) {
  return currencyIds ? currencyIds.map((id) => parseInt(id)) : null;
}

export function newFeeEarnerFilter(feeEarnerIds: string[]) {
  return feeEarnerIds ? feeEarnerIds.map((id) => parseInt(id)) : null;
}

const getFullFieldForEntity = (entity: keyof MatterOrder, order: AscDesc) => {
  switch (entity) {
    case "client":
      return {
        client: {
          name: order,
        },
      };
    case "updatedAt":
      return {
        updatedAt: order,
      };
    case "createdBy":
      return {
        createdBy: {
          displayName: order,
        },
      };
    default:
      return {
        [entity]: order,
      };
  }
};

export function newOrderBy(sort: Sorter): MatterOrder | undefined {
  const { field = "updatedAt", order = AscDesc.desc } = sort || {
    field: "updatedAt",
    order: AscDesc.desc,
  };

  return field ? getFullFieldForEntity(field, order) : undefined;
}

export function newSomeTimeEntriesFilter(someTimeEntries: boolean) {
  return someTimeEntries ? someTimeEntries : null;
}
